/**
 * Character Customizer — pure logic (no I/O, no side effects).
 * Equip toggling, layer/url collection, crop-box math, randomization.
 */

import {
  CATALOG,
  FOCUS,
  LAYER_ORDER,
  RARITY,
  WOMEN_BASE_IDS,
} from './characterCustomizer.constants';
import { getCustomizerAssetUrl } from './characterCustomizer.assets';
import type {
  CustomizerCategoryId,
  CustomizerItem,
  EquippedMap,
  FocusView,
  RarityMeta,
} from './characterCustomizer.types';

export function itemOf(cat: CustomizerCategoryId, id: string | null): CustomizerItem | null {
  if (!id) return null;
  return CATALOG[cat].items.find((it) => it.id === id) ?? null;
}

export function rarityOf(cat: CustomizerCategoryId, id: string | null): RarityMeta {
  const it = itemOf(cat, id);
  return it ? RARITY[it.rarity] : RARITY.common;
}

/**
 * Whether THIS client can actually render the given layered look — i.e. it recognizes the
 * required base. An old app that receives an avatarConfig whose base id was added to the
 * catalog after that app shipped returns false here, so callers fall back to the ready avatar
 * (avatarId) instead of rendering a baseless/broken composite. This is what keeps already-
 * installed apps from breaking or blanking when the catalog grows before they update.
 */
export function canRenderLayeredAvatar(equipped: EquippedMap | null | undefined): boolean {
  if (!equipped) return false;
  return itemOf('base', equipped.base ?? null) != null;
}

/**
 * Categories the current base forbids (shown locked in the rail, force-cleared in state).
 * Women bases ship with built-in hair, so only the hair layer is locked.
 */
export function lockedCategoriesFor(equipped: EquippedMap): Set<CustomizerCategoryId> {
  if (WOMEN_BASE_IDS.has(equipped.base ?? '')) {
    return new Set(['hair']);
  }
  return new Set();
}

/** Women bases ship with built-in hair, so the separate hair layer doesn't apply to them. */
export function isHairLocked(equipped: EquippedMap): boolean {
  return lockedCategoriesFor(equipped).has('hair');
}

/** Enforce base-driven constraints — clear every category the equipped base locks. */
export function applyBaseConstraints(equipped: EquippedMap): EquippedMap {
  const locked = lockedCategoriesFor(equipped);
  if (locked.size === 0) return equipped;
  let next: EquippedMap | null = null;
  for (const cat of locked) {
    if (equipped[cat] !== null) {
      next = next ?? { ...equipped };
      next[cat] = null;
    }
  }
  return next ?? equipped;
}

/**
 * Hair and hat are mutually exclusive (a cap replaces visible hair, and vice-versa),
 * so they can never be worn together. When both are equipped, keep whichever was just
 * equipped and clear the other; with no hint (e.g. randomize), drop the hat.
 */
export function applyLayerExclusions(
  equipped: EquippedMap,
  justEquipped?: CustomizerCategoryId
): EquippedMap {
  if (equipped.hair == null || equipped.hat == null) return equipped;
  const drop: CustomizerCategoryId = justEquipped === 'hat' ? 'hair' : 'hat';
  return { ...equipped, [drop]: null };
}

/**
 * Full normalization: clear base-locked categories first (so a women base drops hair
 * before exclusivity runs), then resolve hair/hat exclusivity. Single chokepoint used
 * by every state transition (equip, randomize, seed).
 */
export function normalizeEquipped(
  equipped: EquippedMap,
  justEquipped?: CustomizerCategoryId
): EquippedMap {
  return applyLayerExclusions(applyBaseConstraints(equipped), justEquipped);
}

/** Equip an item; tapping the equipped item again clears a removable category. */
export function toggleEquip(
  equipped: EquippedMap,
  cat: CustomizerCategoryId,
  id: string | null
): EquippedMap {
  const removable = CATALOG[cat].removable;
  const next = equipped[cat] === id && removable ? null : id;
  return normalizeEquipped({ ...equipped, [cat]: next }, cat);
}

/** Base → front layer URLs for the current look (only categories with an equipped item). */
export function collectEquippedLayers(
  equipped: EquippedMap
): { cat: CustomizerCategoryId; url: string }[] {
  const out: { cat: CustomizerCategoryId; url: string }[] = [];
  for (const cat of LAYER_ORDER) {
    const it = itemOf(cat, equipped[cat]);
    if (it) out.push({ cat, url: getCustomizerAssetUrl(it.assetKey) });
  }
  return out;
}

/** All item art URLs for one category (for prefetch on tab switch). */
export function collectCategoryItemUrls(cat: CustomizerCategoryId): string[] {
  return CATALOG[cat].items.map((it) => getCustomizerAssetUrl(it.assetKey));
}

/**
 * Inner crop-box (pixels) that zooms a square frame of `side` px onto a category's
 * body region. Mirrors the design's FocusTryOn math — the box is scaled up and
 * offset so (cx,cy) lands at the thumbnail center. Pixel math (not percentages) so
 * the negative offsets resolve identically on web and native.
 */
export function getFocusBox(
  focus: FocusView,
  side: number
): { width: number; height: number; left: number; top: number } {
  const size = side * focus.scale;
  return {
    width: size,
    height: size,
    left: (0.5 - focus.cx * focus.scale) * side,
    top: (0.5 - focus.cy * focus.scale) * side,
  };
}

export function focusFor(cat: CustomizerCategoryId): FocusView {
  return FOCUS[cat] ?? { cx: 0.5, cy: 0.5, scale: 1 };
}

/** Total number of catalog items across every category (collection denominator). */
export function totalItemCount(): number {
  let total = 0;
  for (const cat of LAYER_ORDER) total += CATALOG[cat].items.length;
  return total;
}

/**
 * Shop grid column count for the available width. Keeps cells phone-sized while the
 * customizer fills the screen on tablet/desktop (instead of a centered phone column).
 */
export function columnsForWidth(width: number): number {
  if (width >= 1120) return 6;
  if (width >= 840) return 5;
  if (width >= 560) return 4;
  return 3;
}

/**
 * Build a random look. Removable categories have a chance of being left empty.
 * `rng` is injectable for deterministic tests.
 */
export function randomizeFit(
  cats: CustomizerCategoryId[],
  rng: () => number = Math.random
): EquippedMap {
  const next = {} as EquippedMap;
  for (const cat of cats) {
    const { items, removable } = CATALOG[cat];
    if (removable && rng() < 0.28) {
      next[cat] = null;
      continue;
    }
    next[cat] = items.length ? items[Math.floor(rng() * items.length)].id : null;
  }
  return normalizeEquipped(next);
}
